import { getChannelFulfillmentModes } from '@api/channel';
import { getSalesOrder, type SalesOrderDetail, type SalesOrderDispatch, type SalesOrderReturn } from '@api/salesOrders';
import { listWarehousesForPicker, type WarehousePickItem } from '@api/work-order';
import PageHeader from '@components/common/PageHeader';
import { DataSheet, SheetTabStrip, type DataSheetColumn } from '@components/ui/DataSheet';
import { Button, Group, Loader, Text, Tooltip } from '@mantine/core';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { differenceInCalendarDays, isSameDay, subDays } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useB2bOrderWorkspace } from '../b2b/B2bOrderWorkspace';
import { StatusBadge } from '../procurementOrders/shared';
import { formatDateDisplay } from '../PurchaseOrderDetailDrawer';
import { MoneyPair } from './cells';
import { formatRoundMoney as formatMoney, formatUnits } from './format';
import soStyles from './SalesOrders.module.css';
import { formatMoney as formatExactMoney } from '@utils/currency';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */


/** One row of the Lines tab; a dispatch sub-row shows which document shipped the line. */
interface LineRow {
    key: string;
    productId: string;
    label: string;
    sku?: string;
    ordered: number | null;
    dispatched: number;
    received: number | null;
    remaining: number | null;
    status: string | null;
    isDispatch: boolean;
    /** Pricing as the order states it; absent on dispatch sub-rows and unpriced lines. */
    unitPrice?: number | null;
    landingRate?: number | null;
    gstRate?: number | null;
    lineTotal?: number | null;
    lineTotalWithTax?: number | null;
}

interface DispatchLineRow {
    key: string;
    label: string;
    sku?: string;
    ordered: number | null;
    thisDispatch: number;
    cumulative: number;
}

interface ReturnRow extends SalesOrderReturn {
    key: string;
    productLabel: string;
}

type EventKind = 'received' | 'status' | 'acted' | 'dispatch' | 'return' | 'sync';

interface ActivityEvent {
    id: string;
    kind: EventKind;
    at: Date;
    title: string;
    meta: string;
}

const INDIGO = 'rgb(79, 70, 229)';
const AMBER = 'rgb(202, 138, 4)';
const EVENT_COLOR: Record<EventKind, string> = {
    received: semanticColors.positive.primary,
    status: neutralColors.textMuted,
    acted: semanticColors.info.primary,
    dispatch: INDIGO,
    return: semanticColors.critical.primary,
    sync: neutralColors.textMuted,
};
const EVENT_LABEL: Record<EventKind, string> = {
    received: 'Received',
    status: 'Status',
    acted: 'Acted',
    dispatch: 'Dispatch',
    return: 'Debit note',
    sync: 'Sync',
};

const cardStyle: React.CSSProperties = {
    background: neutralColors.surface,
    borderRadius: 12,
    padding: 20,
    border: `1px solid ${neutralColors.border}`,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
};

const formatDateTime = (value: string | Date): string =>
    new Date(value).toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const formatTime = (value: Date): string => value.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

const lineStatusOf = (orderStatus: string, ordered: number, dispatched: number): string => {
    if (orderStatus === 'CANCELLED') return 'CANCELLED';
    if (ordered > 0 && dispatched >= ordered) return 'FULFILLED';
    if (dispatched > 0) return 'PARTIALLY_FULFILLED';
    return 'UNFULFILLED';
};

/* -------------------------------------------------------------------------- */
/*                              Small components                              */
/* -------------------------------------------------------------------------- */

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: neutralColors.textMuted }}>
            {label}
        </span>
        <span style={{ fontSize: 13, fontWeight: 500, color: neutralColors.textPrimary, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {value ?? '—'}
        </span>
    </div>
);

const CardTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <span style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3, color: neutralColors.textPrimary }}>{children}</span>
);

const Chip: React.FC<{ label: string; color: string }> = ({ label, color }) => (
    <span style={{ fontSize: 11, fontWeight: 600, color, background: 'rgb(248, 250, 252)', padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>
        {label}
    </span>
);

/* -------------------------------------------------------------------------- */
/*                              Activity builder                              */
/* -------------------------------------------------------------------------- */

/**
 * The activity feed is a derived union (docs/prds/sales-orders-v1.md, section 7.5): the order
 * header, the status history, the tracker's acted timestamp, each dispatch document, each
 * return line and the last sync. No event table behind it.
 */
const buildActivity = (detail: SalesOrderDetail, warehouseName: (id: string | null) => string): ActivityEvent[] => {
    const events: ActivityEvent[] = [];
    const unitsOrdered = detail.lines.reduce((sum, line) => sum + (line.quantity || 0), 0);

    events.push({
        id: 'received',
        kind: 'received',
        at: new Date(detail.receivedAt),
        title: `Order received from ${detail.channel ?? 'partner'}`,
        meta: [
            `${detail.lines.length} lines`,
            `${formatUnits(unitsOrdered)} units`,
            detail.destinationName ? `destination ${detail.destinationName}` : null,
            detail.expiryDate ? `expires ${formatDateDisplay(detail.expiryDate)}` : null,
        ]
            .filter(Boolean)
            .join(' · '),
    });

    for (const entry of detail.history) {
        events.push({
            id: `status:${entry.status}:${entry.at}`,
            kind: 'status',
            at: new Date(entry.at),
            title: `Partner status: ${entry.status}`,
            meta: 'first seen by the sync',
        });
    }

    if (detail.tracker?.actedAt) {
        const code = detail.tracker.saleOrderCode ?? detail.plan?.planId;
        events.push({
            id: 'acted',
            kind: 'acted',
            at: new Date(detail.tracker.actedAt),
            title: code ? `Plan approved, sale order ${code} pushed to WMS` : 'Plan approved',
            meta: [
                detail.plan ? `${formatUnits(detail.plan.approvedQty)} units approved` : null,
                detail.tracker.expectedDispatchAt ? `expected dispatch ${formatDateDisplay(detail.tracker.expectedDispatchAt)}` : null,
            ]
                .filter(Boolean)
                .join(' · '),
        });
    }

    for (const dispatch of detail.dispatches) {
        events.push({
            id: `dispatch:${dispatch.code ?? dispatch.date}`,
            kind: 'dispatch',
            at: new Date(dispatch.date),
            title: `Dispatched ${formatUnits(dispatch.units)} units`,
            meta: [
                dispatch.code ? `Sale order ${dispatch.code}` : 'No WMS document code',
                `${dispatch.lines.length} lines`,
                dispatch.warehouseId ? `from ${warehouseName(dispatch.warehouseId)}` : null,
            ]
                .filter(Boolean)
                .join(' · '),
        });
    }

    const lineLabel = new Map(detail.lines.map((line) => [line.productId, line.name ?? line.sku ?? line.productId]));
    for (const ret of detail.returns) {
        if (!ret.date) continue;
        events.push({
            id: `return:${ret.id}`,
            kind: 'return',
            at: new Date(ret.date),
            title: `${ret.reference ? `Debit note ${ret.reference}` : 'Return'}: ${formatUnits(ret.quantity)} units${ret.value ? ` · ${formatMoney(ret.value)}` : ''}`,
            meta: [ret.productId ? lineLabel.get(ret.productId) ?? ret.productId : null, ret.reason, ret.disposition].filter(Boolean).join(' · '),
        });
    }

    events.push({
        id: 'sync',
        kind: 'sync',
        at: new Date(detail.lastSyncedAt),
        title: 'Order synced',
        meta: `Partner status ${detail.status}`,
    });

    return events.sort((a, b) => b.at.getTime() - a.at.getTime());
};

const dayLabel = (date: Date): string => {
    const now = new Date();
    if (isSameDay(date, now)) return `Today · ${formatDateDisplay(date.toISOString())}`;
    if (isSameDay(date, subDays(now, 1))) return `Yesterday · ${formatDateDisplay(date.toISOString())}`;
    return formatDateDisplay(date.toISOString());
};

/* -------------------------------------------------------------------------- */
/*                                   Page                                     */
/* -------------------------------------------------------------------------- */

const SalesOrderDetailContainer: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [detail, setDetail] = useState<SalesOrderDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // activity | lines | dispatches | returns, or a b2b-* tab on orders from B2B customers.
    const [tab, setTab] = useState<string>('activity');
    const [reloadKey, setReloadKey] = useState(0);
    const [warehouses, setWarehouses] = useState<WarehousePickItem[]>([]);
    /** Channels marked PO-driven in channel settings - the only ones the fulfil flow can plan for. */
    const [poDrivenKeys, setPoDrivenKeys] = useState<Set<string>>(new Set());

    useEffect(() => {
        listWarehousesForPicker()
            .then(setWarehouses)
            .catch(() => setWarehouses([]));
        getChannelFulfillmentModes()
            .then((modes) =>
                setPoDrivenKeys(new Set(modes.filter((m) => m.fulfillmentMode === 'PO_DRIVEN').map((m) => m.channelAbbreviationKey))),
            )
            .catch(() => setPoDrivenKeys(new Set()));
    }, []);

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        getSalesOrder(id)
            .then((result) => {
                if (!cancelled) setDetail(result);
            })
            .catch((err: unknown) => {
                console.error('Failed to load sales order', err);
                if (!cancelled) setError('Could not load this sales order.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [id, reloadKey]);

    const b2b = useB2bOrderWorkspace(id, detail?.channel, () => setReloadKey((k) => k + 1), setTab);

    const warehouseName = useMemo(() => {
        const byId = new Map(warehouses.map((w) => [w.warehouseId, w.name]));
        return (warehouseId: string | null) => (warehouseId ? byId.get(warehouseId) ?? warehouseId : '—');
    }, [warehouses]);

    // A B2B order also shows its audit records (placed, reviews, cancelled) on the timeline, newest first.
    const activity = useMemo(
        () =>
            detail
                ? [...buildActivity(detail, warehouseName), ...b2b.activity.map((e) => ({ ...e, kind: 'acted' as const }))].sort((a, b) => b.at.getTime() - a.at.getTime())
                : [],
        [detail, warehouseName, b2b.activity],
    );

    const lineRows = useMemo<LineRow[]>(() => {
        if (!detail) return [];
        return detail.lines.map((line) => ({
            key: line.productId,
            productId: line.productId,
            label: line.name ?? line.productId,
            sku: line.sku,
            ordered: line.quantity,
            dispatched: line.dispatchedQuantity,
            received: line.partnerReceivedQuantity ?? 0,
            remaining: Math.max(line.quantity - line.dispatchedQuantity, 0),
            status: lineStatusOf(detail.derivedStatus, line.quantity, line.dispatchedQuantity),
            isDispatch: false,
            unitPrice: line.unitPrice,
            landingRate: line.landingRate,
            gstRate: line.gstRate,
            lineTotal: line.lineTotal,
            lineTotalWithTax: line.lineTotalWithTax,
        }));
    }, [detail]);

    /**
     * The order's value from its priced lines. Shipped value is each line's dispatched units
     * (capped at ordered) at that line's own price, so it is exact where lines differ in price.
     */
    const orderValue = useMemo(() => {
        const priced = (detail?.lines ?? []).filter((line) => line.lineTotal != null);
        if (!detail || priced.length === 0) return null;
        const sum = (pick: (line: (typeof priced)[number]) => number) =>
            Math.round(priced.reduce((total, line) => total + pick(line), 0) * 100) / 100;
        const subtotal = sum((line) => line.lineTotal ?? 0);
        const withTax = priced.every((line) => line.lineTotalWithTax != null) ? sum((line) => line.lineTotalWithTax ?? 0) : null;
        const shipped = sum((line) => Math.min(line.dispatchedQuantity, line.quantity) * (line.unitPrice ?? 0));
        const slabs = new Set(priced.map((line) => line.gstRate ?? null));
        return {
            subtotal,
            withTax,
            tax: withTax === null ? null : Math.round((withTax - subtotal) * 100) / 100,
            slab: slabs.size === 1 ? [...slabs][0] : null,
            shipped,
            open: Math.max(Math.round((subtotal - shipped) * 100) / 100, 0),
            pricedLines: priced.length,
            lines: detail.lines.length,
        };
    }, [detail]);

    /** Expanding a line shows the dispatch documents that shipped it. */
    const dispatchSubRows = useMemo(() => {
        const byProduct = new Map<string, LineRow[]>();
        for (const dispatch of detail?.dispatches ?? []) {
            for (const line of dispatch.lines) {
                const rows = byProduct.get(line.productId) ?? [];
                rows.push({
                    key: `${line.productId}|${dispatch.code ?? dispatch.date}`,
                    productId: line.productId,
                    label: `${dispatch.code ?? 'Dispatch'} · ${formatDateDisplay(dispatch.date)}`,
                    sku: dispatch.warehouseId ? `from ${warehouseName(dispatch.warehouseId)}` : undefined,
                    ordered: null,
                    dispatched: line.quantity,
                    received: null,
                    remaining: null,
                    status: null,
                    isDispatch: true,
                });
                byProduct.set(line.productId, rows);
            }
        }
        return byProduct;
    }, [detail, warehouseName]);

    /** Dispatch blocks newest first, with the running total per SKU computed oldest first. */
    const dispatchBlocks = useMemo(() => {
        if (!detail) return [];
        const orderedByProduct = new Map(detail.lines.map((line) => [line.productId, line]));
        const cumulative = new Map<string, number>();
        const ascending = [...detail.dispatches].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        const blocks = ascending.map((dispatch: SalesOrderDispatch) => {
            const rows: DispatchLineRow[] = dispatch.lines.map((line) => {
                const runningTotal = (cumulative.get(line.productId) ?? 0) + line.quantity;
                cumulative.set(line.productId, runningTotal);
                const ordered = orderedByProduct.get(line.productId);
                return {
                    key: `${dispatch.code ?? dispatch.date}|${line.productId}`,
                    label: line.name ?? ordered?.name ?? line.productId,
                    sku: line.sku ?? ordered?.sku,
                    ordered: ordered?.quantity ?? null,
                    thisDispatch: line.quantity,
                    cumulative: runningTotal,
                };
            });
            return { dispatch, rows };
        });
        return blocks.reverse();
    }, [detail]);

    const returnRows = useMemo<ReturnRow[]>(() => {
        if (!detail) return [];
        const lineLabel = new Map(detail.lines.map((line) => [line.productId, line.name ?? line.sku ?? line.productId]));
        return detail.returns.map((ret) => ({
            ...ret,
            key: ret.id,
            productLabel: ret.productId ? lineLabel.get(ret.productId) ?? ret.productId : '—',
        }));
    }, [detail]);

    /* ------------------------------- Columns -------------------------------- */

    const lineColumns = useMemo<DataSheetColumn<LineRow>[]>(
        () => [
            {
                key: 'label',
                header: 'Product',
                width: 300,
                render: (value: string, row) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.3, paddingLeft: row.isDispatch ? 16 : 0 }}>
                        <span style={{ color: row.isDispatch ? neutralColors.textSecondary : neutralColors.textPrimary, fontWeight: row.isDispatch ? 400 : 500 }}>
                            {value}
                        </span>
                        {row.sku ? <span style={{ fontSize: 11, color: neutralColors.textMuted }}>{row.sku}</span> : null}
                    </div>
                ),
            },
            { key: 'ordered', header: 'Ordered', width: 100, align: 'right', render: (value: number | null) => formatUnits(value) },
            {
                key: 'dispatched',
                header: 'Dispatched',
                width: 110,
                align: 'right',
                render: (value: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatUnits(value)}</span>,
            },
            {
                key: 'received',
                header: 'Received',
                width: 100,
                align: 'right',
                render: (value: number | null) =>
                    value === null ? '' : <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatUnits(value)}</span>,
            },
            {
                key: 'remaining',
                header: 'Remaining',
                width: 100,
                align: 'right',
                render: (value: number | null) =>
                    value === null ? '' : <span style={{ color: value > 0 ? semanticColors.critical.primary : neutralColors.textMuted }}>{formatUnits(value)}</span>,
            },
            {
                key: 'unitPrice',
                header: 'Unit price',
                width: 150,
                align: 'right',
                render: (value: number | null | undefined, row) =>
                    row.isDispatch ? '' : <MoneyPair beforeTax={value} withTax={row.landingRate} />,
            },
            {
                key: 'gstRate',
                header: 'GST',
                width: 70,
                align: 'right',
                render: (value: number | null | undefined, row) => (row.isDispatch ? '' : value == null ? '—' : `${value}%`),
            },
            {
                key: 'lineTotal',
                header: 'Line value',
                width: 160,
                align: 'right',
                render: (value: number | null | undefined, row) =>
                    row.isDispatch ? '' : <MoneyPair beforeTax={value} withTax={row.lineTotalWithTax} />,
            },
            {
                key: 'status',
                header: 'Line status',
                width: 160,
                render: (value: string | null) => (value ? <StatusBadge status={value} /> : ''),
            },
        ],
        [],
    );

    const dispatchColumns = useMemo<DataSheetColumn<DispatchLineRow>[]>(
        () => [
            {
                key: 'label',
                header: 'Product',
                width: 300,
                render: (value: string, row) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.3 }}>
                        <span>{value}</span>
                        {row.sku ? <span style={{ fontSize: 11, color: neutralColors.textMuted }}>{row.sku}</span> : null}
                    </div>
                ),
            },
            { key: 'ordered', header: 'Ordered', width: 100, align: 'right', render: (value: number | null) => formatUnits(value) },
            { key: 'thisDispatch', header: 'This dispatch', width: 120, align: 'right', render: (value: number) => <span style={{ fontWeight: 600 }}>{formatUnits(value)}</span> },
            { key: 'cumulative', header: 'Cumulative', width: 110, align: 'right', render: (value: number) => formatUnits(value) },
        ],
        [],
    );

    const returnColumns = useMemo<DataSheetColumn<ReturnRow>[]>(
        () => [
            { key: 'reference', header: 'Reference', width: 160, render: (value: string | null) => value ?? '—' },
            { key: 'date', header: 'Date', width: 120, render: (value: string | null) => (value ? formatDateDisplay(value) : '—') },
            { key: 'productLabel', header: 'Product', width: 260 },
            { key: 'quantity', header: 'Units', width: 90, align: 'right', render: (value: number) => formatUnits(value) },
            { key: 'value', header: 'Value', width: 110, align: 'right', render: (value: number | null) => (value === null ? '—' : formatMoney(value)) },
            { key: 'reason', header: 'Reason', width: 200, render: (value: string | null) => value ?? '—' },
            { key: 'disposition', header: 'Disposition', width: 140, render: (value: string | null) => value ?? '—' },
        ],
        [],
    );

    /* -------------------------------- Render -------------------------------- */

    if (loading) {
        return (
            <div className="flex w-full flex-col gap-6 page-shell">
                <Group gap="sm">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">Loading sales order…</Text>
                </Group>
            </div>
        );
    }

    if (error || !detail) {
        return (
            <div className="flex w-full flex-col gap-6 page-shell">
                <Button variant={ButtonVariant.DEFAULT} leftSection={<ArrowLeftIcon size={16} />} onClick={() => navigate('/sales-orders')} w="fit-content">
                    Back to sales orders
                </Button>
                <Text c="red">{error ?? 'Could not load this sales order.'}</Text>
            </div>
        );
    }

    const daysToExpiry = detail.expiryDate ? differenceInCalendarDays(new Date(detail.expiryDate), new Date()) : null;
    const expiryHint =
        daysToExpiry === null ? null : daysToExpiry < 0 ? `${-daysToExpiry}d ago` : daysToExpiry === 0 ? 'today' : `in ${daysToExpiry}d`;
    const subtitle = [
        detail.channel,
        detail.destinationName ?? (detail.destinationWarehouseId ? warehouseName(detail.destinationWarehouseId) : null),
        `raised ${formatDateDisplay(detail.orderDate)}`,
        detail.expiryDate ? `expires ${formatDateDisplay(detail.expiryDate)}${expiryHint ? ` (${expiryHint})` : ''}` : null,
    ]
        .filter(Boolean)
        .join(' · ');
    const fillRate = detail.orderedQty > 0 ? Math.round((100 * detail.dispatchedQty) / detail.orderedQty) : null;
    const fillColor =
        fillRate === null ? neutralColors.textMuted : fillRate >= 100 ? semanticColors.positive.primary : fillRate >= 70 ? semanticColors.warning.primary : semanticColors.critical.primary;
    const remaining = Math.max(detail.orderedQty - detail.dispatchedQty, 0);
    const dispatchedFrom = [...new Set(detail.dispatches.map((d) => d.warehouseId).filter(Boolean))].map((wid) => warehouseName(wid)).join(', ');
    const sla = detail.tracker
        ? `${formatDateTime(detail.tracker.actByAt)} · ${detail.tracker.actedAt ? (new Date(detail.tracker.actedAt) <= new Date(detail.tracker.actByAt) ? 'met' : 'late') : detail.tracker.isBreaching ? 'breached' : 'open'}`
        : null;

    // Fulfil (docs/prds/sales-orders-v1.md, section 7.6): Open orders only - nothing dispatched
    // yet - on a PO-driven channel. Partial fulfilment waits for the netting work; the existing
    // plan flow sizes demand from the whole order.
    const canFulfil = detail.derivedStatus === 'OPEN' && !!detail.channel && poDrivenKeys.has(detail.channel);
    const fulfilHref =
        `/distribution?view=track&channel=${encodeURIComponent(detail.channel ?? '')}&po=${encodeURIComponent(detail.purchaseOrderId)}` +
        (detail.destinationWarehouseId ? `&fc=${encodeURIComponent(detail.destinationWarehouseId)}` : '');

    const tabs = [
        { id: 'activity', label: 'Activity' },
        ...b2b.tabs,
        // A B2B order shows its lines on Review & PI and its shipments on Dispatch.
        ...(b2b.tabs.length
            ? []
            : [
                  { id: 'lines', label: `Lines (${detail.lines.length})` },
                  { id: 'dispatches', label: `Dispatches (${detail.dispatches.length})` },
              ]),
        { id: 'returns', label: `Debit notes & returns (${detail.returns.length})` },
    ];

    let grouped: Array<{ day: string; events: ActivityEvent[] }> = [];
    for (const event of activity) {
        const day = dayLabel(event.at);
        const last = grouped[grouped.length - 1];
        if (last && last.day === day) last.events.push(event);
        else grouped = [...grouped, { day, events: [event] }];
    }

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
                <PageHeader icon="🧾" title={detail.purchaseOrderId} subtitle={subtitle} />
                <Group gap="sm" wrap="nowrap">
                    <StatusBadge status={detail.derivedStatus} />
                    {canFulfil ? (
                        <Tooltip
                            label="Opens the distribution plan for this order with its lines as the demand. Approving pushes a sale order coded with this PO number to Unicommerce; on other WMS the plan is recorded and the sale order is keyed by hand."
                            multiline
                            w={320}
                            withArrow
                        >
                            <Button variant={ButtonVariant.ACTION} onClick={() => void navigate(fulfilHref)}>
                                Fulfil order
                            </Button>
                        </Tooltip>
                    ) : null}
                    <Button className={soStyles.compactButton} variant={ButtonVariant.DEFAULT} leftSection={<ArrowLeftIcon size={14} />} onClick={() => navigate('/sales-orders')}>
                        Back to list
                    </Button>
                </Group>
            </Group>

            <div style={{ display: 'flex', gap: 24, flex: '1 1 auto', minHeight: 0, alignItems: 'flex-start' }}>
                {/* Left: the record's tabs */}
                <div
                    style={{
                        flex: '1 1 auto',
                        minWidth: 0,
                        background: neutralColors.surface,
                        borderRadius: 12,
                        border: `1px solid ${neutralColors.border}`,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <SheetTabStrip tabs={tabs} activeTab={tab} onTabChange={(tabId) => setTab(tabId)} />

                    {b2b.isB2bTab(tab) ? b2b.content(tab) : null}

                    {tab === 'activity' && (
                        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column' }}>
                            {grouped.map((group) => (
                                <div key={group.day}>
                                    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', color: neutralColors.textSecondary, padding: '4px 0 12px' }}>
                                        {group.day}
                                    </div>
                                    {group.events.map((event, index) => {
                                        const isLast = index === group.events.length - 1;
                                        return (
                                            <div key={event.id} style={{ display: 'flex', gap: 16 }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 12, flexShrink: 0 }}>
                                                    <span style={{ width: 11, height: 11, borderRadius: '50%', background: EVENT_COLOR[event.kind], marginTop: 4, flexShrink: 0 }} />
                                                    {isLast ? null : <span style={{ width: 1, flex: '1 1 auto', background: 'rgb(226, 232, 240)', marginTop: 4 }} />}
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flex: '1 1 auto', paddingBottom: 20, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                            <span style={{ fontSize: 13, fontWeight: 500, color: neutralColors.textPrimary }}>{event.title}</span>
                                                            <Chip label={EVENT_LABEL[event.kind]} color={EVENT_COLOR[event.kind]} />
                                                        </div>
                                                        {event.meta ? <span style={{ fontSize: 12, lineHeight: 1.4, color: neutralColors.textSecondary }}>{event.meta}</span> : null}
                                                    </div>
                                                    <span style={{ fontSize: 12, color: neutralColors.textMuted, whiteSpace: 'nowrap' }}>{formatTime(event.at)}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    )}

                    {tab === 'lines' && (
                        <DataSheet<LineRow>
                            bare
                            hideHeaderBar
                            data={lineRows}
                            columns={lineColumns}
                            getRowKey={(row) => row.key}
                            getSubRows={(row) => (row.isDispatch ? undefined : dispatchSubRows.get(row.productId))}
                            maxHeight="auto"
                            emptyState="No lines on this order."
                        />
                    )}

                    {tab === 'dispatches' &&
                        (dispatchBlocks.length === 0 ? (
                            <div style={{ padding: 24, fontSize: 13, color: neutralColors.textMuted }}>
                                No dispatch has been recorded against this order. A dispatch appears once the WMS sync sees a sale order carrying this order number.
                            </div>
                        ) : (
                            dispatchBlocks.map(({ dispatch, rows }, index) => (
                                <div key={dispatch.code ?? dispatch.date} style={{ borderTop: index === 0 ? 'none' : `1px solid ${neutralColors.border}` }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px 8px', gap: 16, flexWrap: 'wrap' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: 14, fontWeight: 600, color: neutralColors.textPrimary }}>{dispatch.code ?? 'Dispatch'}</span>
                                            <Chip label="Dispatched" color={INDIGO} />
                                            <span style={{ fontSize: 12, color: neutralColors.textSecondary }}>
                                                {formatDateDisplay(dispatch.date)}
                                                {dispatch.warehouseId ? ` · from ${warehouseName(dispatch.warehouseId)}` : ''}
                                                {` · ${formatUnits(dispatch.units)} units · ${dispatch.lines.length} lines`}
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{ padding: '0 24px 16px' }}>
                                        <DataSheet<DispatchLineRow> bare hideHeaderBar data={rows} columns={dispatchColumns} getRowKey={(row) => row.key} maxHeight="auto" />
                                    </div>
                                </div>
                            ))
                        ))}

                    {tab === 'returns' && (
                        <DataSheet<ReturnRow>
                            bare
                            hideHeaderBar
                            data={returnRows}
                            columns={returnColumns}
                            getRowKey={(row) => row.key}
                            maxHeight="auto"
                            emptyState="No debit notes or returns recorded against this order. Only Blinkit sends a debit-note feed today."
                        />
                    )}
                </div>

                {/* Right rail */}
                <div style={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {b2b.rail}
                    <div style={cardStyle}>
                        <CardTitle>Fulfilment</CardTitle>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                            <Field label="Ordered" value={formatUnits(detail.orderedQty)} />
                            <Field label="Dispatched" value={formatUnits(detail.dispatchedQty)} />
                            <Field label="Remaining" value={<span style={{ color: remaining > 0 ? semanticColors.critical.primary : neutralColors.textPrimary }}>{formatUnits(remaining)}</span>} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: neutralColors.textSecondary }}>
                                <span>Fill rate</span>
                                <span style={{ fontWeight: 600, color: neutralColors.textPrimary }}>{fillRate === null ? '—' : `${fillRate}%`}</span>
                            </div>
                            <div style={{ height: 6, borderRadius: 3, background: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                                <div style={{ width: `${Math.min(fillRate ?? 0, 100)}%`, height: 6, background: fillColor }} />
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, paddingTop: 12, borderTop: `1px solid ${neutralColors.divider}` }}>
                            <Field
                                label="Debit notes / returns"
                                value={detail.returnedUnits > 0 ? `${formatUnits(detail.returnedUnits)} units${detail.returnedValue ? ` · ${formatMoney(detail.returnedValue)}` : ''}` : '0'}
                            />
                            <Field label="Partner status" value={<span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}>{detail.status}</span>} />
                        </div>
                    </div>

                    {orderValue && !b2b.isPortalOrder ? (
                        <div style={cardStyle}>
                            <CardTitle>Order value</CardTitle>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px 16px' }}>
                                <Field label="Subtotal, before tax" value={formatExactMoney(orderValue.subtotal)} />
                                <Field
                                    label={orderValue.slab === null ? 'GST' : `GST ${orderValue.slab}%`}
                                    value={orderValue.tax === null ? '—' : formatExactMoney(orderValue.tax)}
                                />
                                <Field label="Total, with tax" value={orderValue.withTax === null ? '—' : formatExactMoney(orderValue.withTax)} />
                                <Field label="Lines priced" value={`${orderValue.pricedLines} of ${orderValue.lines}`} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, paddingTop: 12, borderTop: `1px solid ${neutralColors.divider}` }}>
                                <Field label="Dispatched, before tax" value={formatExactMoney(orderValue.shipped)} />
                                <Field
                                    label="Still open, before tax"
                                    value={<span style={{ color: orderValue.open > 0 ? semanticColors.critical.primary : neutralColors.textPrimary }}>{formatExactMoney(orderValue.open)}</span>}
                                />
                            </div>
                        </div>
                    ) : null}

                    <div style={cardStyle}>
                        <CardTitle>Record</CardTitle>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px 16px' }}>
                            <Field label="Partner PO" value={detail.purchaseOrderId} />
                            <Field label="Our reference" value={detail.tracker?.saleOrderCode ?? detail.poRefNum ?? '—'} />
                            <Field label="Distribution plan" value={detail.plan ? `${detail.plan.planId.slice(0, 8)} · ${detail.plan.planStatus}` : '—'} />
                            <Field label="Destination" value={detail.destinationName ?? (detail.destinationWarehouseId ? warehouseName(detail.destinationWarehouseId) : '—')} />
                            <Field label="Dispatched from" value={dispatchedFrom || '—'} />
                            <Field label="Order date" value={formatDateDisplay(detail.orderDate)} />
                            <Field label="Expiry" value={detail.expiryDate ? formatDateDisplay(detail.expiryDate) : '—'} />
                            <Field label="Dispatch date" value={detail.dispatchDate ? formatDateDisplay(detail.dispatchDate) : '—'} />
                            <Field
                                label="Act by (SLA)"
                                value={sla ? <span style={{ color: sla.endsWith('breached') || sla.endsWith('late') ? semanticColors.critical.primary : sla.endsWith('met') ? semanticColors.positive.primary : AMBER }}>{sla}</span> : '—'}
                            />
                            <Field label="Last synced" value={formatDateTime(detail.lastSyncedAt)} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SalesOrderDetailContainer;
